import {
  Category,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerServiceItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceBoundItem,
  FinnairServiceItem,
  FinnairServiceSegmentItem,
  SubCategory,
} from '@fcom/dapi/api/models';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';
import { TzDate } from '@fcom/core/utils';

type SeatAssignment = {
  boundId: string;
  segmentId: string;
  passengerId: string;
  seatNumber: string;
};

const createFakeSeat = (assignment: SeatAssignment, currencyCode: string): FinnairPassengerServiceSelectionItem => ({
  id: `CPR-${assignment.segmentId}-${assignment.passengerId}`,
  seatNumber: assignment.seatNumber,
  displayName: '',
  subCategory: SubCategory.SEAT,
  quantity: 1,
  ticketed: true,
  characteristics: ['SYSTEM'],
  totalPrice: { amount: '0', currencyCode },
  unitPrice: { amount: '0', currencyCode },
});

const createFakeSegment = (
  flight: FinnairItineraryItemFlight,
  assignment: SeatAssignment,
  currencyCode: string,
  firstName: string,
  lastName: string
): FinnairServiceSegmentItem => ({
  id: flight.id,
  departure: flight.departure,
  arrival: flight.arrival,
  passengers: [
    {
      id: assignment.passengerId,
      firstName,
      lastName,
      quantity: flight.id === assignment.segmentId ? 1 : 0,
      totalPrice: { amount: '0', currencyCode },
      services: flight.id === assignment.segmentId ? [createFakeSeat(assignment, currencyCode)] : [],
    },
  ],
  quantity: flight.id === assignment.segmentId ? 1 : 0,
  originalTotalPrice: { amount: '0', currencyCode },
  totalPrice: { amount: '0', currencyCode },
});

const updateExistingPassengers = (
  passengers: FinnairPassengerServiceItem[],
  assignment: SeatAssignment,
  currencyCode: string,
  firstName: string,
  lastName: string
): FinnairPassengerServiceItem[] => {
  if (passengers.some((p) => p.id === assignment.passengerId)) {
    return passengers.map((p) =>
      p.id === assignment.passengerId
        ? { ...p, services: p.services.map((s) => ({ ...s, seatNumber: assignment.seatNumber })) }
        : p
    );
  }
  return [
    ...passengers,
    {
      id: assignment.passengerId,
      firstName,
      lastName,
      quantity: 1,
      originalTotalPrice: { amount: '0', currencyCode },
      totalPrice: { amount: '0', currencyCode },
      services: [createFakeSeat(assignment, currencyCode)],
    },
  ];
};

const updateSegmentServices = (
  order: FinnairOrder,
  flight: FinnairItineraryItemFlight,
  segments: FinnairServiceSegmentItem[],
  assignment: SeatAssignment,
  currencyCode: string
): FinnairServiceSegmentItem[] => {
  const passenger = order.passengers.find((p) => p.id === assignment.passengerId);
  const exists = segments.some((s) => s.id === assignment.segmentId);
  if (exists) {
    return segments.map((s) =>
      s.id === assignment.segmentId
        ? {
            ...s,
            passengers: updateExistingPassengers(
              s.passengers || [],
              assignment,
              currencyCode,
              passenger?.firstName,
              passenger?.lastName
            ),
          }
        : s
    );
  }

  return [...segments, createFakeSegment(flight, assignment, currencyCode, passenger?.firstName, passenger?.lastName)];
};

const updateServiceBounds = (
  order: FinnairOrder,
  bounds: FinnairServiceBoundItem[],
  assignment: SeatAssignment,
  currencyCode: string
): FinnairServiceBoundItem[] => {
  const orderBound = order.bounds.find((b) => b.id === assignment.boundId);
  const flight = orderBound?.itinerary?.filter(isFlight).find((flight) => flight.id === assignment.segmentId);

  if (!flight) {
    return bounds;
  }

  if (bounds.some((b) => b.id === assignment.boundId)) {
    return bounds.map((b) =>
      b.id === assignment.boundId
        ? { ...b, segments: updateSegmentServices(order, flight, b.segments || [], assignment, currencyCode) }
        : b
    );
  }
  const passenger = order.passengers.find((p) => p.id === assignment.passengerId);

  return [
    ...bounds,
    {
      id: assignment.boundId,
      segments: [createFakeSegment(flight, assignment, currencyCode, passenger?.firstName, passenger?.lastName)],
      quantity: 1,
      originalTotalPrice: { amount: '0', currencyCode },
      totalPrice: { amount: '0', currencyCode },
    },
  ];
};

const calculateQuantities = (bounds: FinnairServiceBoundItem[]): FinnairServiceBoundItem[] => {
  return bounds.map((b) => {
    const segments = b.segments.map((s) => ({
      ...s,
      quantity: (s.passengers || []).reduce((all, passenger) => all + passenger.services.length, 0),
    }));
    return {
      ...b,
      segments,
      quantity: segments.reduce((sum, s) => sum + s.quantity, 0),
    };
  });
};

export function updateIncludedSeats(
  order: FinnairOrder,
  includedServices: FinnairServiceItem[] = [],
  seatAssignments: SeatAssignment[]
): FinnairServiceItem[] {
  const currencyCode = includedServices[0]?.totalPrice?.currencyCode || 'EUR';
  const other = includedServices.filter((i) => i.category !== Category.SEAT);
  const seatItem: FinnairServiceItem = includedServices.find((i) => i.category === Category.SEAT) || {
    category: Category.SEAT,
    title: 'Seats',
    bounds: [],
    quantity: 0,
    totalPrice: { amount: '0', currencyCode },
  };

  const updatedBounds: FinnairServiceBoundItem[] = seatAssignments.reduce(
    (bounds, assignment) => updateServiceBounds(order, bounds, assignment, currencyCode),
    seatItem.bounds || ([] as FinnairServiceBoundItem[])
  );
  const boundsWithQuantities = calculateQuantities(updatedBounds);
  const totalQuantity = boundsWithQuantities.reduce((sum, b) => sum + b.quantity, 0);

  const newSeatItem = {
    ...seatItem,
    bounds: boundsWithQuantities
      .map((bound) => {
        return {
          ...bound,
          segments: bound.segments.sort((a, b) =>
            TzDate.of(a.departure.dateTime).isBefore(TzDate.of(b.departure.dateTime)) ? -1 : 1
          ),
        };
      })
      .sort((a, b) => {
        return TzDate.of(a.segments[0].departure.dateTime).isBefore(TzDate.of(b.segments[0].departure.dateTime))
          ? -1
          : 1;
      }),
    quantity: totalQuantity,
  };

  return [...other, newSeatItem];
}
